import { useState, useRef, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  UploadSimple, CircleNotch, X, CheckCircle, Warning, ImageSquare, Trash,
} from "@phosphor-icons/react";

const ACCEPTED_MIME = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);
const ACCEPTED_EXT = /\.(jpe?g|png|webp)$/i;
const MAX_BYTES = 15 * 1024 * 1024;   // 15 MB per image
const CONCURRENCY = 3;                 // uploads in parallel
const UNCAT_CAT = "Uncategorized";
const UNCAT_SUB = "General";

// Derive a human-friendly title from a filename.
// "neon_party-1.jpg" -> "Neon Party 1"
function titleFromFilename(name, fallbackIdx) {
  if (!name || typeof name !== "string") return `Style ${fallbackIdx}`;
  const base = name.replace(/\.[^.]+$/, "").replace(/[_\-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!base) return `Style ${fallbackIdx}`;
  const titled = base.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  return titled.slice(0, 120);
}

function isImageFile(f) {
  if (!f) return false;
  if (f.type && ACCEPTED_MIME.has(f.type.toLowerCase())) return true;
  return ACCEPTED_EXT.test(f.name || "");
}

// Ensure a category+subcategory exist in the taxonomy. Creates on the fly
// using the admin endpoints. Returns nothing; throws on unrecoverable error.
async function ensureTaxonomy(loadTaxRaw, targetCat, targetSub) {
  let tax = await loadTaxRaw();
  let cat = tax.find((c) => c.name === targetCat);
  if (!cat) {
    await api.post("/library/categories", { name: targetCat });
    tax = await loadTaxRaw();
    cat = tax.find((c) => c.name === targetCat);
    if (!cat) throw new Error(`Could not create category "${targetCat}".`);
  }
  const hasSub = (cat.subcategories || []).some((s) => s.name === targetSub);
  if (!hasSub) {
    await api.post(`/library/categories/${cat.id}/subcategories`, { name: targetSub });
  }
}

export function BulkStyleUploader({ open, onClose, tax, loadTaxRaw, onDone, currentCat, currentSub }) {
  const [dragOver, setDragOver] = useState(false);
  const [items, setItems] = useState([]);   // { id, file, title, status, reason }
  const [batchCat, setBatchCat] = useState(currentCat || "");
  const [batchSub, setBatchSub] = useState(currentSub || "");
  const [uploading, setUploading] = useState(false);
  const [doneSummary, setDoneSummary] = useState(null); // { ok, skipped, cat, sub }
  const cancelRef = useRef(false);
  const inputRef = useRef(null);

  const batchSubOptions = useMemo(
    () => tax.find((c) => c.name === batchCat)?.subcategories || [],
    [tax, batchCat],
  );

  const resetAll = useCallback(() => {
    setItems([]); setDoneSummary(null); cancelRef.current = false;
  }, []);

  const closeAll = () => {
    if (uploading) return;
    resetAll();
    onClose?.();
  };

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setDoneSummary(null);
    setItems((prev) => {
      const existingKeys = new Set(prev.map((i) => `${i.file.name}::${i.file.size}`));
      const startIdx = prev.length + 1;
      const additions = [];
      incoming.forEach((f, i) => {
        const key = `${f.name}::${f.size}`;
        if (existingKeys.has(key)) return;
        existingKeys.add(key);
        let status = "pending";
        let reason = "";
        if (!isImageFile(f)) { status = "skipped"; reason = "Not an image (jpg/png/webp only)"; }
        else if (f.size > MAX_BYTES) { status = "skipped"; reason = `Too large (>${(MAX_BYTES / 1024 / 1024) | 0} MB)`; }
        additions.push({
          id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
          file: f,
          title: titleFromFilename(f.name, startIdx + i),
          status, reason,
        });
      });
      return [...prev, ...additions];
    });
  }, []);

  const onPick = (e) => { addFiles(e.target.files); if (e.target) e.target.value = ""; };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    addFiles(e.dataTransfer?.files);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!dragOver) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clearAll = () => setItems([]);

  const startUpload = async () => {
    if (uploading) return;
    const queued = items.filter((i) => i.status === "pending");
    if (!queued.length) { toast.error("Add some images first."); return; }

    // Resolve target category+subcategory. Default to Uncategorized/General.
    const targetCat = (batchCat || "").trim() || UNCAT_CAT;
    const targetSub = (batchSub || "").trim() || (batchCat ? (batchSubOptions[0]?.name || UNCAT_SUB) : UNCAT_SUB);

    setUploading(true); cancelRef.current = false; setDoneSummary(null);

    try {
      await ensureTaxonomy(loadTaxRaw, targetCat, targetSub);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not prepare target category.");
      setUploading(false);
      return;
    }

    // Mark queued items as "queued" for visibility
    setItems((prev) => prev.map((i) => (i.status === "pending" ? { ...i, status: "queued" } : i)));

    const pending = [...queued];
    let ok = 0, skipped = items.filter((i) => i.status === "skipped").length;

    const uploadOne = async (item) => {
      if (cancelRef.current) {
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "skipped", reason: "Cancelled" } : i)));
        return;
      }
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "uploading" } : i)));
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("category", targetCat);
        fd.append("subcategory", targetSub);
        fd.append("title", item.title || "Untitled");
        fd.append("source", "bulk_upload");
        await api.post("/library/styles", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "done" } : i)));
      } catch (e) {
        skipped += 1;
        const reason = e?.response?.data?.detail || e?.message || "Upload failed";
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "skipped", reason: String(reason).slice(0, 200) } : i)));
      }
    };

    // Worker pool with concurrency=CONCURRENCY
    const runners = Array.from({ length: Math.min(CONCURRENCY, pending.length) }, async () => {
      while (pending.length && !cancelRef.current) {
        const next = pending.shift();
        if (!next) break;
        await uploadOne(next);
      }
      // On cancel, drain the rest as skipped
      if (cancelRef.current) {
        while (pending.length) {
          const rest = pending.shift();
          if (!rest) break;
          skipped += 1;
          setItems((prev) => prev.map((i) => (i.id === rest.id ? { ...i, status: "skipped", reason: "Cancelled" } : i)));
        }
      }
    });
    await Promise.all(runners);

    setUploading(false);
    setDoneSummary({ ok, skipped, cat: targetCat, sub: targetSub });
    if (ok > 0) {
      toast.success(`Uploaded ${ok} style${ok === 1 ? "" : "s"}${skipped ? ` · Skipped ${skipped}` : ""}.`);
    } else if (skipped > 0) {
      toast.error(`No styles uploaded. Skipped ${skipped}.`);
    }
    onDone?.({ ok, skipped, cat: targetCat, sub: targetSub });
  };

  const cancelUpload = () => { cancelRef.current = true; };

  if (!open) return null;

  const total = items.length;
  const doneCount = items.filter((i) => i.status === "done").length;
  const skippedCount = items.filter((i) => i.status === "skipped").length;
  const activeCount = items.filter((i) => i.status === "uploading").length;
  const pendingCount = items.filter((i) => i.status === "pending" || i.status === "queued").length;
  const pct = total ? Math.round(((doneCount + skippedCount) / total) * 100) : 0;

  return (
    <div
      data-testid="library-bulk-upload-panel"
      className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/[0.06] p-4 mb-5"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <UploadSimple size={18} weight="bold" className="text-fuchsia-400 shrink-0" />
          <h3 className="text-sm font-bold truncate">Bulk upload styles</h3>
          <span className="text-xs text-zinc-500">— select many, or drop a folder of images</span>
        </div>
        <button
          onClick={closeAll}
          disabled={uploading}
          data-testid="library-bulk-close"
          className="h-8 w-8 rounded-md flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-40"
          title="Close"
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      {/* Batch target category (optional) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold mb-1.5 block">
            Category (applies to all) <span className="text-zinc-600">— optional</span>
          </label>
          <select
            data-testid="library-bulk-category"
            value={batchCat}
            onChange={(e) => { setBatchCat(e.target.value); setBatchSub(""); }}
            disabled={uploading}
            className="w-full h-10 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white outline-none focus:border-fuchsia-500/60 disabled:opacity-60"
          >
            <option value="">Uncategorized (default)</option>
            {tax.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-bold mb-1.5 block">
            Subcategory
          </label>
          <select
            data-testid="library-bulk-subcategory"
            value={batchSub}
            onChange={(e) => setBatchSub(e.target.value)}
            disabled={uploading || !batchCat}
            className="w-full h-10 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white outline-none focus:border-fuchsia-500/60 disabled:opacity-60"
          >
            {!batchCat ? (
              <option value="">General (default)</option>
            ) : (
              batchSubOptions.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)
            )}
          </select>
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => !uploading && inputRef.current?.click()}
        data-testid="library-bulk-dropzone"
        className={`mt-4 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
          dragOver ? "border-fuchsia-400 bg-fuchsia-500/10" : "border-white/15 hover:border-fuchsia-500/60 bg-black/30"
        } ${uploading ? "pointer-events-none opacity-60" : ""}`}
      >
        <ImageSquare size={30} weight="duotone" className="mx-auto text-fuchsia-400 mb-2" />
        <p className="text-sm font-semibold text-white">Drop images here or click to select</p>
        <p className="text-xs text-zinc-500 mt-1">
          JPG · PNG · WebP · up to {(MAX_BYTES / 1024 / 1024) | 0} MB each · dozens at a time
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          data-testid="library-bulk-input"
          onChange={onPick}
        />
      </div>

      {/* Queue list */}
      {items.length > 0 && (
        <div className="mt-4 rounded-lg border border-white/10 bg-black/40 overflow-hidden" data-testid="library-bulk-queue">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-xs text-zinc-400">
            <span>
              <strong className="text-white">{total}</strong> file{total === 1 ? "" : "s"} ·
              <span className="ml-1">{doneCount} done</span>
              {skippedCount > 0 && <span className="ml-1">· {skippedCount} skipped</span>}
              {activeCount > 0 && <span className="ml-1">· {activeCount} uploading</span>}
              {pendingCount > 0 && <span className="ml-1">· {pendingCount} pending</span>}
            </span>
            {!uploading && (
              <button
                onClick={clearAll}
                data-testid="library-bulk-clear"
                className="text-zinc-500 hover:text-white transition-colors"
              >Clear list</button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-white/5">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`library-bulk-row-${it.status}`}
                className="flex items-center gap-3 px-3 py-2 text-xs"
              >
                <div className="w-5 shrink-0">
                  {it.status === "done" && <CheckCircle size={16} weight="fill" className="text-emerald-400" />}
                  {it.status === "uploading" && <CircleNotch size={16} className="animate-spin text-fuchsia-400" />}
                  {it.status === "skipped" && <Warning size={16} weight="fill" className="text-amber-400" />}
                  {(it.status === "pending" || it.status === "queued") && <div className="h-2 w-2 rounded-full bg-zinc-600 mx-auto" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-white">{it.title}</p>
                  <p className="truncate text-[11px] text-zinc-500">
                    {it.file.name} · {(it.file.size / 1024).toFixed(0)} KB
                    {it.reason && <span className="text-amber-400/90"> — {it.reason}</span>}
                  </p>
                </div>
                {!uploading && it.status !== "done" && (
                  <button
                    onClick={() => removeItem(it.id)}
                    data-testid={`library-bulk-remove-${it.id}`}
                    className="text-zinc-500 hover:text-red-400 transition-colors"
                    title="Remove from list"
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress bar */}
      {(uploading || doneSummary) && (
        <div className="mt-3">
          <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-fuchsia-500 transition-[width] duration-300"
              style={{ width: `${pct}%` }}
              data-testid="library-bulk-progress"
            />
          </div>
          <p className="mt-1.5 text-xs text-zinc-400">
            {doneCount + skippedCount} of {total} processed
            {doneSummary && (
              <span className="ml-2 text-white">
                — <span className="text-emerald-400 font-semibold">Uploaded {doneSummary.ok}</span>
                {doneSummary.skipped > 0 && <> · <span className="text-amber-400 font-semibold">Skipped {doneSummary.skipped}</span></>}
                <span className="text-zinc-500"> into “{doneSummary.cat} → {doneSummary.sub}”</span>
              </span>
            )}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {!uploading ? (
          <button
            onClick={startUpload}
            disabled={!items.some((i) => i.status === "pending")}
            data-testid="library-bulk-start"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UploadSimple size={16} weight="bold" />
            Upload {items.filter((i) => i.status === "pending").length || ""} image
            {items.filter((i) => i.status === "pending").length === 1 ? "" : "s"}
          </button>
        ) : (
          <button
            onClick={cancelUpload}
            data-testid="library-bulk-cancel"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-red-600/20 border border-red-500/40 hover:bg-red-600/30 text-red-200 text-sm font-bold transition-colors"
          >
            <X size={16} weight="bold" /> Cancel remaining
          </button>
        )}
        <button
          onClick={closeAll}
          disabled={uploading}
          data-testid="library-bulk-close-btn"
          className="h-10 px-4 rounded-full text-sm font-medium text-zinc-400 hover:text-white transition-colors disabled:opacity-40"
        >
          {doneSummary ? "Done" : "Close"}
        </button>
      </div>
    </div>
  );
}

export default BulkStyleUploader;
